import compact from "lodash/compact";
import { LabelIconConfig } from "PFComponents/label/label_icon";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { NumberFilter } from "PFTypes";
import { ComponentProps, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AvailabilityRangeItem } from "./availability_range_item";
import { CheckboxesItem } from "./checkboxes_item";
import { ExpandedFilterWrapper } from "./components/expanded_filter_wrapper";
import { DateItem } from "./date_item";
import { DateRangeItem } from "./date_range_item";
import { AvailabilityFilterItem } from "./filter_items";
import { IntegerItem } from "./integer_item";
import { RadioButtonsItem } from "./radio_buttons_item";
import { ReportingRangeItem } from "./reporting_range_item";
import { SearchItem } from "./search_item";
import { SelectManyItem } from "./select_many_item";
import { SelectOneItem } from "./select_one_item";

// NOTE: this is intentionally loose (matches the previous PropTypes.shape contract) because this
// dispatcher is called with both legacy `Filter`/`FilterWithDefaults` shapes and `FilterV2` shapes,
// which are not structurally compatible with one another.
type AnyFilterFilter = {
  name?: string;
  title: string;
  type: string;
  operator?: string;
  segment?: boolean;
  [key: string]: unknown;
};

export type AnyFilterProps = {
  handleChange: (value: unknown) => void;
  filter: AnyFilterFilter;
  templateKey?: string;
  labelTooltip?: LabelIconConfig;
  [key: string]: unknown;
};

// NOTE: this component dynamically dispatches to differently-typed filter item components based on
// `filter.type`, so the spread props below are cast to bypass structural checks the dispatcher can't
// statically satisfy; each target component still enforces its own prop types when used directly.
export const AnyFilter = (props: AnyFilterProps) => {
  const { t } = useTranslation("filters");
  const { customTypes } = useCustomTypes();

  const isOrderable = customTypes.find((customType) => customType.name === props.filter.name)?.orderable;

  const labelIcons = useMemo(
    () =>
      compact<LabelIconConfig>([
        props.labelTooltip,
        isOrderable && {
          icon: "hierarchical",
          text: t("hierarchicalFilterMessage")
        },
        props.filter.segment && {
          icon: "segment",
          text: t("segmentFilterMessage")
        }
      ]),
    [props.labelTooltip, props.filter.segment, isOrderable, t]
  );

  switch (props.filter.type) {
    case "availability":
      return (
        <AvailabilityFilterItem {...(props as unknown as ComponentProps<typeof AvailabilityFilterItem>)} />
      );
    case "select_many":
      return (
        <SelectManyItem
          {...(props as unknown as ComponentProps<typeof SelectManyItem>)}
          onChange={props.handleChange}
          labelIcons={labelIcons}
        />
      );
    case "select_one":
      return (
        <SelectOneItem
          {...(props as unknown as ComponentProps<typeof SelectOneItem>)}
          onChange={props.handleChange}
          labelIcons={labelIcons}
        />
      );
    case "string":
      return (
        <SearchItem
          {...(props as unknown as ComponentProps<typeof SearchItem>)}
          label={props.filter.title}
          labelIcons={labelIcons}
        />
      );
    case "number":
      return (
        <IntegerItem
          {...(props as unknown as ComponentProps<typeof IntegerItem>)}
          filter={props.filter as unknown as NumberFilter}
          label={props.filter.title}
        />
      );
    case "radio_buttons":
      return (
        <ExpandedFilterWrapper
          {...(props as unknown as ComponentProps<typeof ExpandedFilterWrapper>)}
          onChange={props.handleChange}
          component={RadioButtonsItem}
        />
      );
    case "checkboxes":
      return (
        <ExpandedFilterWrapper
          {...(props as unknown as ComponentProps<typeof ExpandedFilterWrapper>)}
          onChange={props.handleChange}
          component={CheckboxesItem}
        />
      );
    case "date":
      return <DateItem {...(props as unknown as ComponentProps<typeof DateItem>)} />;
    case "date_range":
      return <DateRangeItem {...(props as unknown as ComponentProps<typeof DateRangeItem>)} />;
    case "reporting_range":
      return (
        <ReportingRangeItem
          {...(props as unknown as ComponentProps<typeof ReportingRangeItem>)}
          onChange={props.handleChange}
        />
      );
    case "availability_range":
      return (
        <AvailabilityRangeItem {...(props as unknown as ComponentProps<typeof AvailabilityRangeItem>)} />
      );
    case "hidden":
      return null;
    default:
      return (
        <b style={{ display: "block", padding: 8, border: "1px solid red" }}>{props.filter.type} (??)</b>
      );
  }
};
